import React, { useEffect, useMemo, useState } from 'react'
import type { User } from '@klear/user/user'
import type { UserListGridColDef } from '@klear/user/wire.client'
import {
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ENTITY_USER,
  ROLE_LABELS,
  ROUTE_USER
} from '@klear/constants'
import constant from 'lodash/constant'
import { Link } from 'react-router'
import { useUsersWithActivity, useUserMutations } from '@klear/ui/hooks/use-user'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, ICellRendererParams, ValueGetterParams, GridReadyEvent, GridApi } from 'ag-grid-community'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import DeleteIcon from '@/icons/DeleteIcon'
import AddIcon from '@/icons/AddIcon'
import EditIcon from '@/icons/EditIcon'
import ExportIcon from '@/icons/ExportIcon'
import { KlearDate } from '@klear/date'
import { DeleteButton } from '@klear/ui/components/delete-button'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { Button } from '@klear/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'

import { ProgressStatus, type ProgressStage, calculateProgressScore } from '@klear/ui/components/progress-status'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { ChartContainer, ChartTooltip } from '@klear/ui/components/chart'
import type { ChartConfig } from '@klear/ui/components/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const UsersPage: React.FC = () => {
  useDocumentTitle('Users')
  const { user: currentUser } = useAuth()

  const { users = [], isLoading: usersLoading, error: usersError } = useUsersWithActivity({ fetchAll: true })
  const { traders, isLoading: tradersLoading, error: tradersError } = useTraders()
  const { deleteUser, isDeleting } = useUserMutations()
  const agGrid = useKlearAgGrid()

  const [error] = useState<string | null>(null)
  const [gridApi, setGridApi] = useState<GridApi | null>(null)

  const usersByMonth = useMemo(() => {
    const buckets = new Map<string, { label: string; sort: number; count: number }>()
    for (const u of users) {
      const created = u.accountCreatedAt?.toKlearDate()
      if (!created) continue
      const key = created.toString('YYYY-MM')
      const label = created.toString('MMM YY')
      const entry = buckets.get(key) ?? { label, sort: parseInt(created.toString('YYYYMM'), 10), count: 0 }
      buckets.set(key, { ...entry, count: entry.count + 1 })
    }
    const sorted = [...buckets.values()].sort((a, b) => a.sort - b.sort)
    let running = 0
    return sorted.map(({ label, count }) => {
      running += count
      return { label, count: running }
    })
  }, [users])

  const userChartConfig: ChartConfig = { count: { label: 'Users', color: '#2563eb' } }

  const getTraderOrgName = (traderId: number) => {
    const trader = traders?.find((t) => t.id === traderId)
    return trader?.name ?? `Trader ${traderId}`
  }

  const EmailCellRenderer = (props: ICellRendererParams) => {
    return (
      <a href={`mailto:${props.value}`} className="text-blue-600 hover:text-blue-800">
        {props.value}
      </a>
    )
  }

  const RolesCellRenderer = (props: ICellRendererParams<User>) => {
    const user = props.data
    if (!user || !user.roles || user.roles.length === 0) {
      return <span className="text-sm text-muted-foreground">No roles assigned</span>
    }

    return (
      <div className="flex items-center h-full space-x-4">
        {user.roles.map((role, index) => {
          const isInternal = role.role.startsWith('klear_')
          const orgName = role.traderId ? getTraderOrgName(role.traderId) : null

          return (
            <div key={index} className="flex items-center space-x-1">
              <span
                className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                  isInternal
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                }`}
              >
                {ROLE_LABELS[role.role as keyof typeof ROLE_LABELS]}
              </span>
              {role.traderId !== 1 && <span className="text-sm text-muted-foreground">({orgName})</span>}
            </div>
          )
        })}
      </div>
    )
  }

  const formatRoles = (roles: User['roles']) => {
    if (!roles || roles.length === 0) return 'No roles'

    return roles
      .map((r) => {
        const roleName = ROLE_LABELS[r.role as keyof typeof ROLE_LABELS]
        const traderName = r.traderId ? getTraderOrgName(r.traderId) : ''
        return `${roleName}${traderName ? ` (${traderName})` : ''}`
      })
      .join(', ')
  }

  const ActionCellRenderer = (props: ICellRendererParams<User>) => {
    const user = props.data

    if (!user?.id) {
      return null
    }

    const userIsKlear = user.hasKlearRole
    const canEdit = userIsKlear
      ? currentUser?.isSuperAdmin
      : currentUser?.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_EDIT)
    const canDelete = userIsKlear
      ? currentUser?.isSuperAdmin
      : currentUser?.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_DELETE)

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/${ROUTE_USER}/${user.id}`} className="flex items-center gap-2">
              <AddIcon />
              View
            </Link>
          </DropdownMenuItem>
          {canEdit && (
            <DropdownMenuItem asChild>
              <Link to={`/${ROUTE_USER}/${user.id}/edit`} className="flex items-center gap-2">
                <EditIcon />
                Edit
              </Link>
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DeleteButton
              resourceId={user.id}
              resourceName={user.fullName}
              resourceType={PERMISSION_ENTITY_USER}
              deleteMutation={deleteUser}
              isDeleting={isDeleting}
              wrapperComponent={DropdownMenuItem}
            >
              <span className="flex items-center gap-2 text-red-600">
                <DeleteIcon />
                Delete
              </span>
            </DeleteButton>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const ComputeUserActivationStages = (user: User): ProgressStage[] => {
    const welcomeEmailComplete = user.welcomeEmailSent ?? false
    const loginComplete = !!(user.firstLoginAt || user.hasLoggedIn)
    const accountSetupComplete = welcomeEmailComplete && loginComplete

    let accountSetupWarning: string | undefined = undefined
    if (user.accountCreatedAt && !accountSetupComplete) {
      const daysSinceCreation = KlearDate.daysBetween(user.accountCreatedAt.toKlearDate(), KlearDate.today())
      if (daysSinceCreation >= 30) {
        accountSetupWarning = `Account created ${daysSinceCreation} ${daysSinceCreation === 1 ? 'day' : 'days'} ago`
      }
    }

    return [
      {
        title: 'Account setup',
        steps: [
          {
            title: 'Welcome Email',
            complete: welcomeEmailComplete,
            date: user.welcomeEmailSentAt?.toKlearDate()
          },
          {
            title: 'Login',
            complete: loginComplete,
            date: user.firstLoginAt?.toKlearDate()
          }
        ],
        status: accountSetupWarning ? ('warning' as const) : undefined,
        message: accountSetupWarning
      },
      {
        title: 'Accepted Legals',
        steps: [
          {
            title: 'Terms of Service',
            complete: user.termsOfServiceAccepted ?? false
          },
          {
            title: 'Privacy Policy',
            complete: user.privacyPolicyAccepted ?? false
          }
        ]
      }
    ]
  }

  const StatusCellRenderer = (props: ICellRendererParams<User>) => {
    const user = props.data
    if (!user) {
      return <span className="text-muted-foreground">-</span>
    }

    const stages = ComputeUserActivationStages(user)

    return (
      <div className="flex items-center h-full">
        <ProgressStatus stages={stages} size="small" />
      </div>
    )
  }

  const columnDefs: (ColDef<User> & UserListGridColDef)[] = [
    {
      headerName: 'Name',
      valueGetter: (params: ValueGetterParams<User>) => params.data?.fullName ?? '',
      sortable: true,
      filter: 'agTextColumnFilter',
      pinned: 'left',
      lockPosition: true,
      minWidth: 200,
      cellDataType: 'text',
      colId: 'name',
      sortKeys: ['firstName', 'lastName']
    },
    {
      headerName: 'Email',
      field: 'email',
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: EmailCellRenderer,
      cellDataType: 'text'
    },
    {
      headerName: 'Roles',
      field: 'roles',
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: RolesCellRenderer,
      valueGetter: (params: ValueGetterParams<User>) => formatRoles(params.data?.roles ?? []),
      cellDataType: 'text'
    },
    {
      headerName: 'Created',
      field: 'accountCreatedAt',
      minWidth: 150
    },
    {
      headerName: 'Activation',
      colId: 'status',
      sortable: true,
      filter: false,
      cellRenderer: StatusCellRenderer,
      valueGetter: (params: ValueGetterParams<User>) => {
        const user = params.data
        if (user == null) return 0
        const stages = ComputeUserActivationStages(user)
        return calculateProgressScore(stages)
      },
      cellDataType: 'number',
      minWidth: 200
    },
    {
      headerName: 'Last Login',
      field: 'lastLoginAt',
      minWidth: 150
    },
    {
      headerName: '',
      colId: 'actions',
      type: 'actions',
      cellRenderer: ActionCellRenderer,
      pinned: 'right',
      lockPosition: true,
      menuTabs: [],
      valueGetter: constant(null),
      cellStyle: { overflow: 'visible' },
      flex: 0,
      width: 60,
      minWidth: 60,
      maxWidth: 60
    }
  ]

  const defaultColDef = {
    ...agGrid.defaultColDef,
    flex: 1,
    minWidth: 150
  }

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
  }

  const onFilterTextBoxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', e.target.value)
    }
  }

  const handleExport = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: `users_export_${KlearDate.today().toJSON()}.csv`,
        columnKeys: ['name', 'email', 'roles', 'status', 'accountCreatedAt', 'lastLoginAt']
      })
    }
  }

  useEffect(() => {
    if (!gridApi) return
    if (usersLoading || tradersLoading) {
      gridApi.showLoadingOverlay()
      return
    }
    if (users && users.length > 0) {
      gridApi.hideOverlay()
    } else {
      gridApi.showNoRowsOverlay()
    }
  }, [gridApi, usersLoading, tradersLoading, users])

  if (usersError || tradersError) {
    return (
      <div className="p-4 text-red-500 bg-red-100 dark:bg-red-950 dark:text-red-300 rounded-md">
        Error: {usersError?.message || tradersError?.message}
      </div>
    )
  }

  if (error) {
    return <div className="p-4 text-red-500 bg-red-100 dark:bg-red-950 dark:text-red-300 rounded-md">{error}</div>
  }

  return (
    <div className="container mx-auto px-4 py-4 h-full flex flex-col gap-4 min-h-0">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Users</h1>
        <Link
          to={`/${ROUTE_USER}/new`}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
        >
          <AddIcon />
          <span>Add User</span>
        </Link>
      </div>

      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search users by name, email, role, or trader..."
          onChange={onFilterTextBoxChanged}
          className="flex-grow px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center space-x-2"
          disabled={!gridApi || users.length === 0}
        >
          <ExportIcon />
          <span>Export CSV</span>
        </button>
      </div>

      {usersByMonth.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Cumulative Users by Month</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-4">
            <ChartContainer config={userChartConfig} className="h-[180px] w-full">
              <BarChart data={usersByMonth} margin={{ left: 10, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                <YAxis hide allowDecimals={false} />
                <ChartTooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  formatter={(value) => [`${value}`, 'Users']}
                  labelFormatter={(label) => label as string}
                />
                <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {users.length === 0 && !usersLoading ? (
        <p className="text-muted-foreground text-center py-8">No users found matching your search criteria.</p>
      ) : (
        <>
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="w-full h-full min-h-[600px]">
              <AgGridReact<User>
                rowData={users}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                columnTypes={agGrid.columnTypes}
                dataTypeDefinitions={agGrid.dataTypeDefinitions}
                animateRows={true}
                rowSelection="multiple"
                pagination={true}
                paginationPageSize={20}
                theme={agGrid.theme}
                onGridReady={onGridReady}
                quickFilterText=""
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default UsersPage
